import React from 'react';

import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { ContentWithAction2 } from '@/components/shared/my24/content-with-action-2';
import { styled } from '@/stitches.config';
import { IHeadline, IItem, IMy24Tracking } from '@/types/shared';

const StyledContentWrap = styled(Box, {
  padding: '$space-4 $space-4',
  '@xs': {
    padding: '$space-4 $space-6',
  },
  '@md': {
    padding: '$space-6 $space-10',
  },
  '@lg': {
    padding: '$space-6 $space-6',
    '& i': {
      display: 'none',
    },
  },
  '@xl': {
    padding: '$space-6 $space-8',
    '& i': {
      display: 'inline-block',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    opacity: 0.8,
    backgroundImage:
      'linear-gradient(rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.7) 100%)',
    '@lg': {
      backgroundImage:
        'linear-gradient(rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0.7) 100%)',
    },
    pointerEvents: 'none',
  },
});

const StyledSharedCTA = styled(Section, {
  [`${StyledContentWrap}`]: {
    '&::before': {
      opacity: 0.8,
      background:
        'linear-gradient(rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.7) 100%)',
      '@lg': {
        background:
          'linear-gradient(rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0.7) 100%)',
      },
    },
  },
  variants: {
    theme: {
      einstein: {
        backgroundColor: '#1A1A1A',
        [`${StyledContentWrap}`]: {
          '&::before': {
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, #000000 100%)',
            '@lg': {
              opacity: 0.8,
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0) 52%, rgba(0, 0, 0, 0.95) 100%)',
            },
          },
        },
        '& .shared-cta-item': {
          clipPath: 'inset(0% round 4px)',
          '@lg': {
            clipPath: 'inset(0% round 8px)',
          },
        },
      },
      dark: {
        backgroundColor: '$gray200',
      },
      light: {
        backgroundColor: '$white',
      },
    },
  },
  defaultVariants: {
    theme: 'light',
  },
});

export interface SharedCTAProps
  extends React.ComponentProps<typeof StyledSharedCTA> {
  active?: boolean;
  headline: IHeadline;
  items?: IItem[];
  dataTrack?: IMy24Tracking;
}

const CTASection: React.FC<SharedCTAProps> = ({
  active,
  theme,
  headline: { title },
  items,
  dataTrack,
}: SharedCTAProps) => {
  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {!!active && items?.length > 0 && (
        <StyledSharedCTA
          size="2xl"
          test="section_cta"
          data-impression="cta"
          theme={theme}
        >
          <Container>
            {!!title && (
              <Text as="h2" size="text-h2" sr-only>
                {title}
              </Text>
            )}
            <Grid
              templateColumns={{ '@initial': '1', '@lg': '3' }}
              gap={{ '@initial': '0', '@2xs': '6' }}
              data-test="cta_items"
            >
              {items?.map((item, index) => (
                <Link
                  key={index}
                  title={item?.preTitle}
                  href={item?.buttons?.[0]?.url}
                  target={item?.buttons?.[0]?.target}
                  tracking={{
                    dataTrack: dataTrack as string,
                    dataTrackSection: 'cta',
                    dataTrackText: item?.buttons?.[0]?.label,
                    dataTrackValue: item?.preTitle,
                    dataTrackUrl: item?.buttons?.[0]?.url,
                  }}
                  test={{
                    dataTest: `cta_item_${index}`,
                  }}
                  className="shared-cta-item"
                >
                  <Box position="relative" data-hover>
                    {!!item?.image?.src && (
                      <Image
                        hoverEffect="zoom"
                        rootMargin="20% 0px 0px 0px"
                        src={item?.image?.src}
                        alt={item?.image?.alt}
                        width={600}
                        height={338}
                        layout="responsive"
                        aspectRatio={{ '@initial': '16by9', '@lg': '3by4' }}
                        sources={[
                          {
                            srcset: item?.imageDesktop?.src as string,
                            media: { minWidth: 1024 },
                            options: { width: 414, crop: '3:4' },
                          },
                          ...generateTabletAndMobileImageSources({
                            mobileSrc: item?.image?.src,
                            mobileOptions: {
                              crop: '16:9',
                            },
                          }),
                        ]}
                      />
                    )}
                    {!!item?.title && (
                      <StyledContentWrap
                        position="absolute"
                        inset="0"
                        pointerEvents="none"
                        display="flex"
                        alignItems="end"
                      >
                        <ContentWithAction2
                          item={item}
                          dataTest="cta"
                          dataTrack={dataTrack}
                          size="sm"
                          theme="dark"
                          type="with-icon"
                          css={{
                            gap: '$space-0',
                            '@xs': {
                              gap: '$space-6',
                            },
                            '@hover': {
                              transition:
                                'transform var(--transition-duration) var(--transition-easing)',
                              '[data-hover]:hover &': {
                                transform: 'translateY(-25%)',
                              },
                            },
                          }}
                        />
                      </StyledContentWrap>
                    )}
                  </Box>
                </Link>
              ))}
            </Grid>
          </Container>
        </StyledSharedCTA>
      )}
    </>
  );
};
CTASection.displayName = 'CTASection';
export default CTASection;
